<template>
  <RevDrawer
    :name="MODAL_NAMES.KONBINI_SETTINGS"
    :title="i18n(translations.title)"
    @open="handleModalOpen"
  >
    <template #body>
      <RevLoadingScreen
        v-if="pending"
        class="mt-8"
        :text="i18n(translations.loading)"
      />
      <RevForm
        v-if="!pending"
        :initial-values="initialFormValues"
        :validate="validate()"
        @submit="handleSubmit"
      >
        <template #default="{ values, errors, hasError }">
          <h4 class="body-1-bold mb-56">
            {{ i18n(translations.subtitle) }}
          </h4>
          <RevInputText
            id="storeOperationManager"
            v-model="values.storeOperationManager"
            class="sm:mt-8 sm:w-full md:mr-8 md:grow"
            :error="errors.storeOperationManager"
            :label="i18n(translations.storeOperationManager)"
          />
          <RevInputText
            id="storeSecurityOfficer"
            v-model="values.storeSecurityOfficer"
            class="sm:mt-8 sm:w-full md:mr-8 md:grow"
            :error="errors.storeSecurityOfficer"
            :label="i18n(translations.storeSecurityOfficer)"
          />
          <RevInputPhone
            id="konbiniPhoneNumber"
            v-model="values.konbiniPhoneNumber"
            class="sm:mt-8 sm:w-full md:mr-8 md:grow"
            :clear-button-aria-label="i18n(translations.inputClear)"
            :default-country="market.countryCode"
            description=""
            :error="errors.konbiniPhoneNumber"
            :label="i18n(translations.konbiniPhoneNumber)"
            :translate="i18n.country"
          />
          <RevInputText
            id="konbiniEmail"
            v-model="values.konbiniEmail"
            class="sm:mt-8 sm:w-full md:mr-8 md:grow"
            :error="errors.konbiniEmail"
            :label="i18n(translations.konbiniEmail)"
            type="email"
          />
          <RevInfoBlock
            class="mb-12 mt-24"
            :icon="IconWarning"
            :title="i18n(translations.konbiniInfoBlockContent)"
            variant="warning"
          />

          <RevButton
            :disabled="hasError"
            full-width="always"
            :loading="isSubmitting"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.save) }}
          </RevButton>
        </template>
      </RevForm>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputPhone } from '@ds/components/InputPhone'
import { RevInputText } from '@ds/components/InputText'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import { REGEXP_VALID_EMAIL } from '~/constants/regex'

import { MODAL_NAMES } from '../../../Payment.constants'

import { DEFAULT_FORM_VALUES } from './ModalKonbiniSettings.constants'
import translations from './ModalKonbiniSettings.translations'
import type { FormValues } from './ModalKonbiniSettings.types'

const i18n = useI18n()
const { openErrorToast, openSuccessToast } = useTheToast()
const isSubmitting = ref(false)
const { market } = useMarketplace()

const {
  data: konbiniSettings,
  pending,
  execute: fetchKonbiniSettings,
  error,
} = useHttpFetch(merchantsAPI.getPaymentMethodsKonbiniSettings, {
  immediate: false,
})

if (error.value) {
  openErrorToast()
  closeModal(MODAL_NAMES.KONBINI_SETTINGS)
}

const initialFormValues = computed(() => ({
  ...DEFAULT_FORM_VALUES,
  ...konbiniSettings.value,
}))

function validate() {
  return makeValidate<FormValues>({
    konbiniEmail: [
      required(i18n(translations.required)),
      matchingRegExp(REGEXP_VALID_EMAIL, i18n(translations.emailInvalid)),
    ],

    konbiniPhoneNumber: [required(i18n(translations.required))],
    storeOperationManager: [required(i18n(translations.required))],
    storeSecurityOfficer: [required(i18n(translations.required))],
  })
}

function handleModalOpen() {
  fetchKonbiniSettings()
}

async function handleSubmit(values: FormValues) {
  isSubmitting.value = true
  try {
    await $httpFetch(merchantsAPI.putPaymentMethodsKonbiniSettings, {
      body: values,
    })

    openSuccessToast({
      content: i18n(translations.konbiniSettingsUpdateSuccessMessage),
    })

    closeModal(MODAL_NAMES.KONBINI_SETTINGS)
  } catch {
    openErrorToast()
  } finally {
    isSubmitting.value = false
  }
}
</script>
